/**
 * SalesOrderDetailModal -- Modal de solo lectura para ver el detalle de un pedido de venta.
 * Secciones: Info, Lineas, Totales, Notas, Aprobacion, Facturas Vinculadas.
 * Acciones segun estado: Aprobar, Facturar, Anular.
 */
import { formatMoneyForTenant, useTenantCurrency } from '@/lib/locale'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { X, Check, FileText, XCircle, User, Calendar, DollarSign, Paperclip, Download, Eye, AlertCircle, ExternalLink } from 'lucide-react'
import { DataTable } from '@/components/ds/DataTable'
import { useSalesOrder, useApproveSalesOrder, useInvoiceSalesOrder, useCancelSalesOrder, useOrderAttachments, useSalesOrderComments, useCreateSalesOrderComment, openSalesOrderPdf } from '../hooks/useSales'
import { AnularPedidoDialog } from './AnularPedidoDialog'
import { usePermission } from '@/hooks/usePermission'
import { useAuthStore } from '@/store/authStore'
import { extractErrorMessage } from '@/lib/errorUtils'
import { displayLabelCls as labelCls } from '@/lib/formStyles'
import { useTenantTax } from '@/modules/config/hooks/useConfig'
import { Modal } from '@/components/ds/Modal'

interface Props {
  orderId: string
  onClose: () => void
}

const REF_DOC_LABELS: Record<string, string> = {
  '33': 'Factura Electrónica',
  '34': 'Factura No Afecta o Exenta',
  '39': 'Boleta Electrónica',
  '41': 'Boleta No Afecta o Exenta',
  '48': 'Comprobante de Pago Electrónico',
  '52': 'Guía de Despacho',
  '56': 'Nota de Débito',
  '61': 'Nota de Crédito',
  '801': 'Orden de Compra',
  '802': 'Nota de Pedido',
  '803': 'Contrato',
  '804': 'Resolución',
  'HES': 'H. Entrada de Servicios',
}

const COD_REF_LABELS: Record<string, string> = {
  '1': 'Anula documento',
  '2': 'Corrige texto',
  '3': 'Corrige monto',
}

const STATUS_CONFIG: Record<string, { cls: string; dotCls: string; label: string }> = {
  draft:     { cls: 'bg-surface-muted text-content-muted',     dotCls: 'bg-content-subtle',    label: 'Borrador' },
  approved:  { cls: 'bg-primary-50 text-primary-hover',        dotCls: 'bg-primary',     label: 'Aprobado' },
  invoiced:  { cls: 'bg-success-surface text-success-content',  dotCls: 'bg-success-solid',  label: 'Facturado' },
  cancelled: { cls: 'bg-danger-surface text-danger-content',          dotCls: 'bg-danger-solid',      label: 'Anulado' },
}

const fieldCls = 'w-full px-3 py-2 text-sm border border-divider rounded-lg bg-surface-muted text-content min-h-[38px]'
const sectionTitleCls = 'text-xs font-semibold text-content-subtle uppercase tracking-wider mb-3'

const formatCLP = (val: string | number) =>
  formatMoneyForTenant(typeof val === 'string' ? parseFloat(val) || 0 : val)

function Field({ label, value }: { label: string; value?: string | null }) {
  return (
    <div>
      <p className={labelCls}>{label}</p>
      <div className={fieldCls}>{value || '\u2014'}</div>
    </div>
  )
}

// Estados de SaleDocument (factura), distintos de los del pedido.
const DOC_STATUS_CLS: Record<string, string> = {
  accepted: 'bg-success-surface text-success-content',
  sent:     'bg-sky-50 dark:bg-sky-900/30 text-sky-700 dark:text-sky-300',
  draft:    'bg-surface-muted text-content-muted',
  rejected: 'bg-danger-surface text-danger-content',
  cancelled:'bg-danger-surface text-danger-content',
}

export function SalesOrderDetailModal({ orderId, onClose }: Props) {
  const navigate = useNavigate()
  const tax = useTenantTax()
  const baseCurrency = useTenantCurrency()
  const { data: order, isLoading } = useSalesOrder(orderId)
  const { data: attachmentsData } = useOrderAttachments(orderId)
  const approveMutation = useApproveSalesOrder()
  const invoiceMutation = useInvoiceSalesOrder()
  const cancelMutation = useCancelSalesOrder()

  const { can } = usePermission()
  const isSellerOnly = !can('sales.orders', 'approve')
  const canApprove = can('sales.orders', 'approve')

  // `ApproveSalesOrderView` rechaza con 403 aprobar tu propio pedido
  // (segregación de funciones): tener el permiso no alcanza. Se oculta el botón
  // y se explica por qué, en vez de ofrecer una acción que siempre rebota.
  const { user } = useAuthStore()
  const esPedidoPropio = !!user?.id && !!order && order.seller === user.id

  const { data: commentsData } = useSalesOrderComments(orderId)
  const createComment = useCreateSalesOrderComment(orderId)
  const [commentText, setCommentText] = useState('')

  const [showCancelConfirm, setShowCancelConfirm] = useState(false)
  const [actionError, setActionError] = useState<string | null>(null)

  const submitComment = (type: 'comment' | 'correction_request') => {
    const text = commentText.trim()
    if (!text) return
    createComment.mutate({ text, comment_type: type }, {
      onSuccess: () => setCommentText(''),
      onError: (e) => setActionError(extractErrorMessage(e, 'No se pudo registrar el comentario.')),
    })
  }

  const statusCfg = STATUS_CONFIG[order?.status ?? 'draft'] || STATUS_CONFIG.draft

  const handleApprove = () => {
    setActionError(null)
    approveMutation.mutate(orderId, {
      onError: (e) => setActionError(extractErrorMessage(e, 'No se pudo aprobar el pedido.')),
    })
  }

  const handleInvoice = () => {
    setActionError(null)
    invoiceMutation.mutate(orderId, {
      onError: (e) => setActionError(extractErrorMessage(e, 'No se pudo facturar el pedido.')),
    })
  }

  const handleCancel = (reason: string) => {
    setActionError(null)
    cancelMutation.mutate(
      { id: orderId, reason },
      {
        onSuccess: () => {
          setShowCancelConfirm(false)
        },
        onError: (e) => {
          setShowCancelConfirm(false)
          setActionError(extractErrorMessage(e, 'No se pudo anular el pedido.'))
        },
      },
    )
  }

  return (
    <Modal
      open
      onClose={onClose}
      size="xl"
      className="relative"
      title={
        <span className="flex items-center gap-2">
          <FileText size={18} className="text-content-subtle shrink-0" />
          Pedido {order?.order_number ? `#${order.order_number}` : ''}
        </span>
      }
      subtitle={order && (
        <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium ${statusCfg.cls}`}>
          <span className={`w-1.5 h-1.5 rounded-full ${statusCfg.dotCls}`} />
          {statusCfg.label}
        </span>
      )}
      actions={
        <>
          {order && (
            <button
              onClick={() => { void openSalesOrderPdf(order.id) }}
              className="mr-auto inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-content-muted bg-soft border border-divider rounded-lg hover:bg-canvas transition-colors"
              title="Descargar cotización en PDF"
            >
              <Download size={13} />
              PDF
            </button>
          )}
          {order && (order.status === 'draft' || order.status === 'approved') && (
            <>
              {order.status === 'draft' && !isSellerOnly && esPedidoPropio && (
                <p className="text-xs text-content-muted">
                  Tu propio pedido lo tiene que aprobar otra persona.
                </p>
              )}

              {order.status === 'draft' && !isSellerOnly && !esPedidoPropio && (
                <button
                  onClick={handleApprove}
                  disabled={approveMutation.isPending}
                  className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-success-fill rounded-lg hover:bg-success-fill-hover disabled:opacity-50 transition-colors"
                >
                  <Check size={14} />
                  {approveMutation.isPending ? 'Aprobando...' : 'Aprobar'}
                </button>
              )}

              {order.status === 'approved' && !isSellerOnly && (
                <button
                  onClick={handleInvoice}
                  disabled={invoiceMutation.isPending}
                  className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-on-primary bg-primary rounded-lg hover:bg-primary-hover disabled:opacity-50 transition-colors"
                >
                  <FileText size={14} />
                  {invoiceMutation.isPending ? 'Facturando...' : 'Facturar'}
                </button>
              )}

              <button
                onClick={() => setShowCancelConfirm(true)}
                className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-danger-content-soft bg-danger-surface border border-danger-border rounded-lg hover:bg-danger-surface-strong transition-colors"
              >
                <XCircle size={14} />
                Anular
              </button>
            </>
          )}
        </>
      }
    >
      <div className="space-y-6">
        {isLoading && (
          <div className="space-y-3 animate-pulse">
            <div className="h-8 bg-surface-muted rounded" />
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-10 bg-surface-muted rounded" />
            ))}
          </div>
        )}

        {order && !isLoading && (
          <>
            {/* -- Info -- */}
            <div>
              <p className={sectionTitleCls}>Informacion del Pedido</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field
                  label="Cliente"
                  value={
                    order.customer_name
                      ? `${order.customer_name}${order.customer_tax_id ? ` (${order.customer_tax_id})` : ''}`
                      : null
                  }
                />
                <Field label="Vendedor" value={order.seller_name} />
                <Field label="Fecha Pedido" value={order.order_date} />
                <Field label="Fecha Entrega" value={order.expected_delivery_date} />
                <Field
                  label="Forma de Pago"
                  value={order.payment_method === 'credito' ? `Credito (${order.payment_term_days || 0} dias)` : 'Contado'}
                />
                <Field label="Tipo Documento" value={order.target_document_type} />
              </div>
            </div>

            {/* -- Lineas -- */}
            <div>
              <p className={sectionTitleCls}>Lineas del Pedido</p>
              <DataTable<any>
                columns={[
                  { key: 'sku', header: 'SKU',
                    render: line => (
                      <span className="font-mono text-content-muted text-xs">{line.sku || '\u2014'}</span>
                    ) },
                  { key: 'name', header: 'Nombre',
                    render: line => <span className="text-content font-medium">{line.name}</span> },
                  { key: 'unit', header: 'Unidad',
                    render: line => <span className="text-content-muted text-xs">{line.unit || '\u2014'}</span> },
                  { key: 'quantity', header: 'Cantidad', numeric: true,
                    render: line => <span className="text-content">{line.quantity}</span> },
                  { key: 'unit_price', header: 'Precio Unit.', numeric: true,
                    render: line => <span className="text-content">{formatCLP(line.unit_price)}</span> },
                  { key: 'discount_pct', header: 'Desc %', numeric: true,
                    render: line => (
                      <span className="text-content-muted">
                        {line.discount_pct && parseFloat(line.discount_pct) > 0
                          ? `${line.discount_pct}%`
                          : '\u2014'}
                      </span>
                    ) },
                  { key: 'subtotal', header: 'Subtotal', numeric: true,
                    render: line => (
                      <span className="font-semibold text-content">
                        {line.subtotal ? formatCLP(line.subtotal) : '\u2014'}
                        {line.price_currency && line.price_currency !== baseCurrency && line.subtotal_original && (
                          <span className="block text-xs font-normal text-content-subtle">
                            {line.price_currency} {parseFloat(line.subtotal_original).toLocaleString('es-CL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </span>
                        )}
                      </span>
                    ) },
                  { key: 'commission_pct', header: 'Comision %', numeric: true,
                    render: line => (
                      <span className="text-content-muted">
                        {line.commission_pct != null && parseFloat(line.commission_pct) > 0
                          ? `${line.commission_pct}%`
                          : '\u2014'}
                      </span>
                    ) },
                ]}
                rows={order.lines ?? []}
                rowKey={(_, i) => i}
                minWidth={960}
                caption="Líneas del pedido de venta"
              />
            </div>

            {/* -- Totales -- */}
            <div className="flex justify-end gap-6 text-sm">
              <div className="text-right">
                <p className="text-xs text-content-subtle">Subtotal</p>
                <p className="font-medium text-content">{formatCLP(order.subtotal)}</p>
              </div>
              <div className="text-right">
                <p className="text-xs text-content-subtle">{tax.name}</p>
                <p className="font-medium text-content">{formatCLP(order.tax_amount)}</p>
              </div>
              <div className="text-right">
                <p className="text-xs text-content-subtle">Total</p>
                <p className="text-lg font-bold text-content">{formatCLP(order.total)}</p>
              </div>
              {order.commission_total != null && parseFloat(order.commission_total) > 0 && (
                <div className="text-right">
                  <p className="text-xs text-primary">Comision Total</p>
                  <p className="font-medium text-primary">{formatCLP(order.commission_total)}</p>
                </div>
              )}
            </div>

            {/* -- Tipos de cambio + total en moneda extranjera (informativo) -- */}
            {order.exchange_rate_data &&
              Object.keys(order.exchange_rate_data).some(k => !['locked_at', 'is_locked', 'lock_source'].includes(k)) && (
                <div className="flex justify-end">
                  <div className="bg-primary-50 border border-primary-200 rounded-lg px-3 py-2 text-xs space-y-1">
                    <p className="font-semibold text-primary-hover">
                      Tipos de cambio {(order.exchange_rate_data.is_locked as any) === true || (order.exchange_rate_data.is_locked as any) === 'true' ? '(bloqueados)' : '(indicativos)'}
                    </p>
                    {Object.entries(order.exchange_rate_data)
                      .filter(([k]) => !['locked_at', 'is_locked', 'lock_source'].includes(k))
                      .map(([code, rate]) => {
                        const r = Number(rate)
                        return (
                          <div key={code} className="text-primary">
                            <div>1 {code} = {formatCLP(String(rate))} CLP</div>
                            {r > 0 && Number(order.total) > 0 && (
                              <div className="font-semibold">
                                Total: {code} {(Number(order.total) / r).toLocaleString('es-CL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                              </div>
                            )}
                          </div>
                        )
                      })}
                    <p className="text-[11px] text-warning-content-soft italic">Totales en CLP</p>
                  </div>
                </div>
              )}

            {/* -- Referencias -- */}
            {order.referencias && order.referencias.length > 0 && (
              <div>
                <p className={sectionTitleCls}>Referencias</p>
                <DataTable<typeof order.referencias[number]>
                  columns={[
                    { key: 'tipo_doc', header: 'Tipo Doc',
                      render: ref => (
                        <span className="text-content">
                          <span className="font-mono">{ref.tipo_doc}</span>
                          {REF_DOC_LABELS[ref.tipo_doc] ? ` — ${REF_DOC_LABELS[ref.tipo_doc]}` : ''}
                        </span>
                      ) },
                    { key: 'folio_ref', header: 'Folio Ref',
                      render: ref => <span className="text-content">{ref.folio_ref || '—'}</span> },
                    { key: 'fecha_ref', header: 'Fecha Ref',
                      render: ref => <span className="text-content-muted">{ref.fecha_ref || '—'}</span> },
                    { key: 'cod_ref', header: 'Código',
                      render: ref => (
                        <span className="text-content-muted">
                          {ref.cod_ref ? `${ref.cod_ref} — ${COD_REF_LABELS[ref.cod_ref] || ''}` : '—'}
                        </span>
                      ) },
                    { key: 'razon_ref', header: 'Razón',
                      render: ref => <span className="text-content-muted">{ref.razon_ref || '—'}</span> },
                  ]}
                  rows={order.referencias}
                  rowKey={(_, i) => i}
                  minWidth={640}
                  caption="Documentos a los que el pedido hace referencia"
                />
              </div>
            )}

            {/* -- Campos Personalizados -- */}
            {order.personalizados && order.personalizados.length > 0 && (
              <div>
                <p className={sectionTitleCls}>Campos Personalizados</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {order.personalizados.map((campo, i) => (
                    <div key={i}>
                      <p className={labelCls}>{campo.nombre}</p>
                      <div className={fieldCls}>
                        {campo.valor === 'true' ? 'Sí' : campo.valor === 'false' ? 'No' : campo.valor || '—'}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* -- Contactos de Cobranza -- */}
            {order.collection_contacts && order.collection_contacts.length > 0 && (
              <div>
                <p className={sectionTitleCls}>Contactos de Cobranza</p>
                <div className="flex flex-wrap gap-2">
                  {order.collection_contacts.map((email, i) => (
                    <span key={i} className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs bg-sky-50 dark:bg-sky-900/30 text-sky-700 dark:text-sky-300 border border-sky-100 dark:border-sky-900">
                      <span className="w-1.5 h-1.5 rounded-full bg-sky-400" />
                      {email}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* -- Notas -- */}
            {order.notes && (
              <div>
                <p className={sectionTitleCls}>Notas</p>
                <div className={fieldCls + ' whitespace-pre-wrap'}>{order.notes}</div>
              </div>
            )}

            {/* -- Aprobacion -- */}
            {order.status !== 'draft' && order.approved_by && (
              <div>
                <p className={sectionTitleCls}>Aprobacion</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <Field label="Aprobado por" value={order.approved_by_name ?? order.approved_by} />
                  <Field label="Fecha de aprobacion" value={order.approved_at} />
                </div>
              </div>
            )}

            {/* -- Facturas Vinculadas -- */}
            {order.invoices && order.invoices.length > 0 && (
              <div>
                <p className={sectionTitleCls}>Facturas Vinculadas</p>
                <div className="space-y-2">
                  {order.invoices.map((inv: any) => {
                    const cls = DOC_STATUS_CLS[inv.status] || 'bg-surface-muted text-content-muted'
                    return (
                      <div
                        key={inv.id}
                        onClick={() => { onClose(); navigate(`/sales/documents?highlight=${inv.id}`) }}
                        className="flex items-center justify-between px-3 py-2 rounded-lg border border-divider hover:bg-soft cursor-pointer transition-colors"
                        title="Ver factura en Documentos de Venta"
                      >
                        <div className="flex items-center gap-3">
                          <FileText size={14} className="text-content-subtle" />
                          <span className="text-sm font-medium text-content inline-flex items-center gap-1">
                            Folio #{inv.folio}
                            <ExternalLink size={11} className="text-content-subtle" />
                          </span>
                          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${cls}`}>
                            {inv.status_display || inv.status}
                          </span>
                        </div>
                        <span className="text-sm font-semibold text-content">
                          {formatCLP(inv.total)}
                        </span>
                      </div>
                    )
                  })}
                </div>
              </div>
            )}

            {/* -- Historial / Comentarios -- */}
            <div>
              <p className={sectionTitleCls}>Historial de interacciones</p>
              {commentsData?.comments?.length ? (
                <div className="space-y-2 mb-3">
                  {commentsData.comments.map(c => (
                    <div
                      key={c.id}
                      className={`rounded-lg border p-3 ${
                        c.comment_type === 'correction_request'
                          ? 'border-warning-border bg-warning-surface'
                          : 'border-divider bg-glass-3'
                      }`}
                    >
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs font-medium text-content">
                          {c.author_name}
                          {c.comment_type === 'correction_request' && (
                            <span className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-warning-surface-strong text-warning-content">
                              Solicitud de corrección
                            </span>
                          )}
                        </span>
                        <span className="text-[11px] text-content-subtle">{c.created_at?.slice(0, 16).replace('T', ' ')}</span>
                      </div>
                      <p className="text-sm text-content-muted whitespace-pre-wrap">{c.text}</p>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-xs text-content-subtle mb-3">Sin interacciones registradas.</p>
              )}

              {order.status !== 'cancelled' && (
                <div className="space-y-2">
                  <textarea aria-label="Comentario"
                    value={commentText}
                    onChange={e => setCommentText(e.target.value)}
                    rows={2}
                    placeholder={canApprove ? 'Escribir un comentario o solicitar correcciones al vendedor…' : 'Escribir un comentario…'}
                    className="w-full border border-input-border rounded-lg px-3 py-2 text-sm bg-input-bg text-content focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                  <div className="flex justify-end gap-2">
                    <button
                      type="button"
                      onClick={() => submitComment('comment')}
                      disabled={!commentText.trim() || createComment.isPending}
                      className="px-3 py-1.5 text-xs border border-divider rounded-lg hover:bg-soft disabled:opacity-50"
                    >
                      Comentar
                    </button>
                    {canApprove && (
                      <button
                        type="button"
                        onClick={() => submitComment('correction_request')}
                        disabled={!commentText.trim() || createComment.isPending}
                        className="inline-flex items-center gap-1 px-3 py-1.5 text-xs bg-warning-fill text-white rounded-lg hover:bg-warning-fill-hover disabled:opacity-50"
                      >
                        <AlertCircle size={13} /> Solicitar correcciones
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* -- Adjuntos -- */}
            {attachmentsData && attachmentsData.count > 0 && (
              <div>
                <p className={sectionTitleCls}>Adjuntos ({attachmentsData.count})</p>
                <div className="space-y-1.5">
                  {attachmentsData.attachments.map(att => (
                    <div key={att.id} className="flex items-center gap-3 px-3 py-2 rounded-lg border border-divider hover:bg-soft transition-colors">
                      <Paperclip size={13} className="text-content-subtle flex-shrink-0" />
                      <span className="text-xs text-content flex-1 truncate">{att.file_name}</span>
                      <span className="text-[10px] text-content-subtle">{(att.file_size / 1024).toFixed(1)} KB</span>
                      <a
                        href={att.download_url}
                        target="_blank"
                        rel="noreferrer"
                        className="p-1 text-content-subtle hover:text-primary transition-colors"
                        title="Ver"
                      >
                        <Eye size={13} />
                      </a>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Cancel confirmation overlay */}
      {showCancelConfirm && order && (
        <AnularPedidoDialog
          orderNumber={order.order_number}
          isPending={cancelMutation.isPending}
          onConfirm={handleCancel}
          onClose={() => setShowCancelConfirm(false)}
        />
      )}

      {/* Action error */}
      {actionError && (
        <div className="mt-4 flex items-start gap-2.5 px-4 py-3 rounded-lg bg-danger-surface border border-danger-border text-sm text-danger-content">
          <AlertCircle size={16} className="flex-shrink-0 mt-0.5" />
          <span className="flex-1">{actionError}</span>
          <button onClick={() => setActionError(null)} className="flex-shrink-0 text-danger-solid hover:text-danger-content transition-colors">
            <X size={14} />
          </button>
        </div>
      )}

    </Modal>
  )
}
